import React, { useState, useCallback } from 'react'
import {
    Button,
    Card,
    CardHeader,
    Container,
    Row,
    Col,
} from "reactstrap";
import Header from 'components/Headers/Header'
import TrialBanner from 'components/Trial/TrialBanner'
import BoardsCountdownBanner from 'components/Trial/BoardsCountdownBanner'
import AddonCrossSellBanner from 'components/Billing/AddonCrossSellBanner'
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Spinner } from 'reactstrap';
import { Redirect } from 'react-router-dom';
import { hasPaidAccess, hasActiveAccess } from 'config/subscriptionPlans';

const PDF_URL = 'https://firebasestorage.googleapis.com/v0/b/pmr-bank.firebasestorage.app/o/review-guides%2Fpmr-review-guide.pdf?alt=media&token=16f56a3a-a2ff-41dd-b2a0-7db2270a93ee';
const WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js';

const FREE_PREVIEW_PAGES = 10;

const PmrReviewGuide = (props) => {
    const history = useHistory();
    const isPaid = hasPaidAccess(props.userDetails);
    const hasAccess = hasActiveAccess(props.userDetails);
    const isTrialUser = hasAccess && !isPaid;

    const [currentPage, setCurrentPage] = useState(0);

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        CurrentPageInput,
                        EnterFullScreen,
                        GoToNextPage,
                        GoToPreviousPage,
                        NumberOfPages,
                        ShowSearchPopover,
                        Zoom,
                        ZoomIn,
                        ZoomOut,
                        SwitchTheme,
                    } = slots;
                    return (
                        <div style={{ alignItems: 'center', display: 'flex', width: '100%' }}>
                            <div style={{ padding: '0px 2px' }}><ShowSearchPopover /></div>
                            <div style={{ padding: '0px 2px' }}><GoToPreviousPage /></div>
                            <div style={{ padding: '0px 2px', display: 'flex', alignItems: 'center' }}>
                                <CurrentPageInput /> <span style={{ padding: '0 4px' }}>/</span> <NumberOfPages />
                            </div>
                            <div style={{ padding: '0px 2px' }}><GoToNextPage /></div>
                            <div style={{ padding: '0px 2px', marginLeft: 'auto' }}><ZoomOut /></div>
                            <div style={{ padding: '0px 2px' }}><Zoom /></div>
                            <div style={{ padding: '0px 2px' }}><ZoomIn /></div>
                            <div style={{ padding: '0px 2px' }}><EnterFullScreen /></div>
                            <div style={{ padding: '0px 2px' }}><SwitchTheme /></div>
                        </div>
                    );
                }}
            </Toolbar>
        ),
    });

    const handlePageChange = useCallback((e) => {
        setCurrentPage(e.currentPage);
    }, []);

    // Replaces actual page content with a lock placeholder for pages beyond the limit.
    // The canvas/text/annotation layers are never rendered — nothing to delete in DevTools.
    const renderPage = useCallback((renderPageProps) => {
        const { pageIndex, width, height, canvasLayer, textLayer, annotationLayer, markRendered } = renderPageProps;
        const isLocked = isTrialUser && pageIndex >= FREE_PREVIEW_PAGES;

        if (isLocked) {
            // Tell the viewer this page is "rendered" so it doesn't wait forever
            markRendered(pageIndex);
            return (
                <div
                    style={{
                        width: `${width}px`,
                        height: `${height}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        userSelect: 'none',
                    }}
                >
                    <i className="fas fa-lock text-warning" style={{ fontSize: '36px', marginBottom: '16px' }}></i>
                    <h4 className="text-dark mb-2">Page {pageIndex + 1} Locked</h4>
                    <p className="text-muted mb-3 text-center" style={{ maxWidth: '300px', fontSize: '14px' }}>
                        Your free trial includes the first {FREE_PREVIEW_PAGES} pages.
                    </p>
                    <Button
                        color="success"
                        size="sm"
                        onClick={() => history.push('/client/plans')}
                    >
                        <i className="fas fa-crown mr-1"></i>
                        Subscribe to Unlock
                    </Button>
                </div>
            );
        }

        // Normal page — render all layers
        return (
            <>
                {canvasLayer.children}
                {textLayer.children}
                {annotationLayer.children}
            </>
        );
    }, [isTrialUser, history]);

    // Only accessible when PM&R specialty is active
    if (props.activeSpecialty !== 'pmr') {
        return <Redirect to="/client/question-bank/create-test" />;
    }

    // No access at all (expired trial, no subscription)
    if (!hasAccess) {
        return (
            <>
                <Header />
                <Container className="mt--7" fluid>
                    <Row>
                        <div className="col">
                            <Card className="shadow">
                                <CardHeader className="border-0">
                                    <div className='float-left'>
                                        <h5 className="h3 mb-0">PM&R Review Guide</h5>
                                    </div>
                                </CardHeader>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px' }}>
                                    <i className="fas fa-lock text-warning" style={{ fontSize: '40px', marginBottom: '15px' }}></i>
                                    <h3 className="text-dark mb-2">Access Required</h3>
                                    <p className="text-muted mb-3 text-center" style={{ maxWidth: '300px', fontSize: '14px' }}>
                                        Subscribe to access the PM&R Review Guide.
                                    </p>
                                    <Button
                                        color="success"
                                        onClick={() => history.push('/client/plans')}
                                    >
                                        <i className="fas fa-crown mr-2"></i>
                                        View Plans
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <TrialBanner />
                <BoardsCountdownBanner />
                <AddonCrossSellBanner />
                {isTrialUser && (
                    <Row className="mb-3">
                        <Col lg="12">
                            <div className="alert alert-info shadow mb-0" style={{ borderLeft: '4px solid #11cdef' }}>
                                <i className="fas fa-book-reader text-info mr-2"></i>
                                <strong>Preview Mode:</strong>{' '}
                                You can preview the first {FREE_PREVIEW_PAGES} pages during your free trial. Subscribe to unlock the full guide.
                            </div>
                        </Col>
                    </Row>
                )}
                <Row>
                    <div className="col">
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <div className='float-left'>
                                    <h5 className="h3 mb-0">PM&R Review Guide</h5>
                                </div>
                                {isTrialUser && (
                                    <div className='float-right'>
                                        <span className="text-muted small">
                                            Page {currentPage + 1} {currentPage < FREE_PREVIEW_PAGES ? `of ${FREE_PREVIEW_PAGES} preview` : ''}
                                        </span>
                                    </div>
                                )}
                            </CardHeader>
                            <Row className="pl-4 pr-4">
                                <Col lg='12'>
                                    <div style={{ height: '1000px' }} className='mb-4'>
                                        <Worker workerUrl={WORKER_URL}>
                                            <Viewer
                                                fileUrl={PDF_URL}
                                                plugins={[defaultLayoutPluginInstance]}
                                                defaultScale={1}
                                                onPageChange={handlePageChange}
                                                renderPage={isTrialUser ? renderPage : undefined}
                                                renderLoader={(percentages) => (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                        <Spinner color="primary" />
                                                        <p className="mt-2 text-muted">Loading {Math.round(percentages)}%</p>
                                                    </div>
                                                )}
                                            />
                                        </Worker>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </div>
                </Row>
            </Container>
        </>
    );
};

const mapStateToProps = state => {
    return {
        userDetails: state.loginAuth.userDetails,
        activeSpecialty: state.specialty.activeSpecialty,
    };
};

export default connect(mapStateToProps)(PmrReviewGuide);
